import { normalizeRole } from '@/lib/rbac';

export const PRIVILEGED_MFA_ROLES = ['SYSTEM_ADMIN', 'TENANT_ADMIN'] as const;

/** These routes remain usable while an administrator is completing MFA. */
export const MFA_ENFORCEMENT_SAFE_PATHS = [
  '/login',
  '/logout',
  '/auth/logout',
  '/auth/signout',
  '/auth/callback',
  '/auth/mfa',
  '/auth/recovery',
  '/forgot-password',
  '/reset-password',
  '/help',
  '/help-centre',
  '/help-center',
  '/legal',
  '/privacy',
  '/terms',
  '/support',
  '/api/auth/password-login',
  '/api/auth/sso/lookup',
  '/api/auth/diagnostics',
  '/api/mobile/auth/password-reset/request',
  '/api/mobile/auth/password-reset/verify',
  '/api/mobile/auth/password-reset/update',
  '/api/mobile/app-version-policy',
] as const;

export type AssuranceLevel = 'aal1' | 'aal2' | null | undefined;

export function isPrivilegedMfaRole(role: string | null | undefined): boolean {
  const normalizedRole = normalizeRole(role);
  return PRIVILEGED_MFA_ROLES.some((candidate) => candidate === normalizedRole);
}

export function shouldEnforceAdminMfa(
  role: string | null | undefined,
  tenantRequiresAdminMfa: boolean,
): boolean {
  return tenantRequiresAdminMfa && isPrivilegedMfaRole(role);
}

export function isMfaEnforcementSafePath(pathname: string): boolean {
  return MFA_ENFORCEMENT_SAFE_PATHS.some(
    (safePath) => pathname === safePath || pathname.startsWith(`${safePath}/`),
  );
}

export function adminMfaRedirectPath(input: {
  role: string | null | undefined;
  tenantRequiresAdminMfa?: boolean;
  pathname: string;
  search?: string;
  assuranceCheckFailed?: boolean;
  currentLevel?: AssuranceLevel;
  nextLevel?: AssuranceLevel;
}): string | null {
  if (
    !shouldEnforceAdminMfa(input.role, input.tenantRequiresAdminMfa === true) ||
    isMfaEnforcementSafePath(input.pathname)
  ) {
    return null;
  }

  const requestedPath = `${input.pathname}${input.search ?? ''}`;
  const next = requestedPath === '/' ? '' : `?next=${encodeURIComponent(requestedPath)}`;
  const nextWithExistingQuery =
    requestedPath === '/' ? '' : `&next=${encodeURIComponent(requestedPath)}`;

  if (input.assuranceCheckFailed) {
    return `/auth/mfa/enroll?reason=verification_unavailable${nextWithExistingQuery}`;
  }
  if (input.currentLevel === 'aal2') return null;
  if (input.nextLevel === 'aal2') return `/login?mfa=1${nextWithExistingQuery}`;
  return `/auth/mfa/enroll${next}`;
}
